/**
 * OHIF Viewer Utilities
 * Helper functions pentru deschidere OHIF viewer optimizat
 */

import api from '../services/api';

/**
 * Obține Hanging Protocol recomandat pentru un studiu
 * @param studyInstanceUID - DICOM Study Instance UID
 * @returns Promise cu hanging_protocol_id sau null
 */
async function getHangingProtocolForStudy(studyInstanceUID: string): Promise<string | null> {
  try {
    const response = await api.get(`/studies/${studyInstanceUID}/hanging-protocol`);
    if (response.data?.hanging_protocol_id) {
      console.log(`[OHIF] HP pentru ${studyInstanceUID}: ${response.data.hanging_protocol_id} (${response.data.matched_by})`);
      return response.data.hanging_protocol_id;
    }
  } catch (error) {
    console.warn('[OHIF] Nu s-a putut obține HP, folosind default:', error);
  }
  return null;
}

/**
 * Deschide OHIF viewer în popup maximized
 * 
 * @param studyInstanceUID - DICOM Study Instance UID
 * @param seriesInstanceUIDs - Optional: DICOM Series Instance UIDs (pentru deschidere directă serie)
 * @param options - Opțiuni suplimentare pentru popup
 * @returns Window reference la popup (sau null dacă blockat de browser)
 */
export async function openOHIFViewer(
  studyInstanceUID: string,
  seriesInstanceUIDs?: string[],
  options?: {
    pacsUrl?: string;
    autoFullscreen?: boolean;
    reuseWindow?: boolean;
    hangingProtocolId?: string;  // Poate fi furnizat explicit
  }
): Promise<Window | null> {
  const {
    pacsUrl,
    autoFullscreen = false,
    reuseWindow = true,
    hangingProtocolId: explicitHpId
  } = options || {};

  // Obține Hanging Protocol (explicit sau din backend)
  let hangingProtocolId = explicitHpId;
  if (!hangingProtocolId) {
    hangingProtocolId = await getHangingProtocolForStudy(studyInstanceUID) || undefined;
  }

  // Construiește URL-ul OHIF
  let url: string;
  const hpParam = hangingProtocolId ? `&hangingProtocolId=${encodeURIComponent(hangingProtocolId)}` : '';
  
  if (pacsUrl) {
    // PACS extern (SMR, Affidea, etc.)
    const seriesParam = seriesInstanceUIDs?.length 
      ? `&SeriesInstanceUIDs=${seriesInstanceUIDs.join(',')}` 
      : '';
    url = `/pacs/${encodeURIComponent(pacsUrl)}/webviewer/viewer?StudyInstanceUIDs=${studyInstanceUID}${seriesParam}${hpParam}`;
  } else {
    // PACS default ZenRIS
    const seriesParam = seriesInstanceUIDs?.length 
      ? `&SeriesInstanceUIDs=${seriesInstanceUIDs.join(',')}` 
      : '';
    url = `/webviewer/viewer?StudyInstanceUIDs=${studyInstanceUID}${seriesParam}${hpParam}`;
  }

  // Dimensiuni ecran
  const screenWidth = window.screen.availWidth;
  const screenHeight = window.screen.availHeight;

  // Specs pentru popup maximized
  const windowFeatures = [
    `width=${screenWidth}`,
    `height=${screenHeight}`,
    'left=0',
    'top=0',
    'resizable=yes',
    'scrollbars=yes',
    'toolbar=no',
    'menubar=no',
    'location=no',
    'status=yes'
  ].join(',');

  // Window name (pentru reuse)
  const windowName = reuseWindow ? 'zenris_ohif_viewer' : '_blank';

  // Deschide popup
  const popup = window.open(url, windowName, windowFeatures);

  if (!popup) {
    // Popup blocat de browser
    console.warn('[OHIF] Popup blocat. Fallback la tab nou.');
    window.open(url, '_blank');
    return null;
  }

  // Focus pe popup
  popup.focus();

  // Auto-fullscreen (dacă cerut)
  if (autoFullscreen) {
    popup.addEventListener('load', () => {
      if (popup.document?.documentElement?.requestFullscreen) {
        popup.document.documentElement.requestFullscreen().catch((err) => {
          console.warn('[OHIF] Fullscreen refuzat:', err);
        });
      }
    });
  }

  return popup;
}

/**
 * Verifică dacă un popup OHIF este deschis
 * @returns true dacă popup-ul există și nu e închis
 */
export function isOHIFViewerOpen(): boolean {
  const popup = window.open('', 'zenris_ohif_viewer');
  if (popup && !popup.closed) {
    popup.close(); // Închide verificarea
    return true;
  }
  return false;
}

/**
 * Închide popup-ul OHIF (dacă există)
 */
export function closeOHIFViewer(): void {
  const popup = window.open('', 'zenris_ohif_viewer');
  if (popup && !popup.closed) {
    popup.close();
  }
}
